import { useEffect, useRef, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { registerStepUpPrompter } from '@/api/step-up';

interface Pending {
  invalid: boolean;
  resolve: (code: string | null) => void;
}

const CODE = /^\d{6}$/;

/**
 * ko-admin#313 / ko-backend#1059 — the dialog that asks for a fresh TOTP code.
 *
 * Registered once in the shell, so any screen's action can need one without
 * knowing it: `apiRequest` meets the 403, asks here, and sends the same request
 * again with the code. The form behind stays as it was — nothing closes, and a
 * cancel lands as the error the screen already shows.
 *
 * The codes are single-use per 30-second step, so two sensitive actions in the
 * same step with the same code refuse the second: the error says to wait for
 * the next one.
 */
export function StepUpDialog() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [code, setCode] = useState('');
  const pendingRef = useRef<Pending | null>(null);

  useEffect(
    () =>
      registerStepUpPrompter(
        ({ invalid }) =>
          new Promise<string | null>((resolve) => {
            // A second request asking while one is open answers the first with
            // a cancel rather than leaving it waiting for ever.
            pendingRef.current?.resolve(null);
            const next = { invalid, resolve };
            pendingRef.current = next;
            setCode('');
            setPending(next);
          }),
      ),
    [],
  );

  const finish = (value: string | null) => {
    pending?.resolve(value);
    pendingRef.current = null;
    setPending(null);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (CODE.test(code)) finish(code);
  };

  return (
    <Dialog.Root open={pending !== null} onOpenChange={(o) => !o && finish(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[60] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-surface p-5">
          <Dialog.Title className="text-lg font-semibold text-text-primary">
            Confirma con tu código
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-text-muted">
            Esta acción pide un código reciente de tu app de autenticación. Lo que estabas haciendo
            se envía en cuanto lo confirmes.
          </Dialog.Description>
          <form onSubmit={onSubmit} className="mt-4 space-y-3">
            <div className="space-y-1">
              <label
                htmlFor="step-up-code"
                className="text-xs uppercase tracking-wide text-text-muted"
              >
                Código de 6 dígitos
              </label>
              <input
                id="step-up-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="h-10 w-full rounded-lg border border-border bg-graphite px-3 text-center font-mono text-lg tracking-[0.4em] text-text-primary focus-visible:border-gold/60 focus-visible:outline-none"
              />
            </div>
            {pending?.invalid && (
              <p role="alert" className="text-sm text-red">
                El código es incorrecto o ya se usó. Espera al siguiente código de tu app y vuelve a
                intentarlo.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => finish(null)}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={!CODE.test(code)}>
                Confirmar
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
